import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert, Button, Col, ColorPicker, Drawer, Empty, Input, Radio, Row, Select, Space, Spin, Typography, message, theme,
} from 'antd';
import { SaveFilled, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    listReportFunctions,
    executeReportFunction,
} from '../../../actions/reportingActions';
import { IconCode, IconReload } from './reportingIcons';
import ChartRenderer from './ChartRenderer';
import { inferChartEncoding } from './reportingSqlUtils';
import { buildChartExecuteFilter, enrichVizSpec } from './reportingChartUtils';
import { listDatasetDimensions, processChartDataset } from './reportingChartCache';
import useCategoryLabelMaps from './useCategoryLabelMaps';
import {
    SINGLE_METRIC_VIZ,
    STAT_VIZ,
    normalizeVolumeEncoding,
    encodingHasVolume,
} from './chartVizTypes';
import {
    detectDatasetShape,
    HIERARCHY_VIZ_TYPES,
    isHierarchicalDataset,
    vizOptionsForShape,
} from './reportingDatasetShape';
import {
    STAT_DISPLAY_AUTO,
    STAT_DISPLAY_BREAKDOWN,
    STAT_DISPLAY_TOTAL,
} from './reportingStatCard';

const { Text } = Typography;

const FN_LIST_W = 268;

const DEFAULT_SERIES_COLORS = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272'];

const DISPLAY_MODE_OPTIONS = [
    { value: 'table', label: 'Table' },
    { value: 'chart', label: 'Chart' },
];

function columnOptions(columns) {
    return columns.map(c => ({ value: c.key, label: c.label || c.key }));
}

export default function AddChartDrawer({
    open,
    onClose,
    onSave,
    dataSourceId,
    schema,
    chartToEdit = null,
    dateRange = null,
    globalFilters = {},
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { token } = theme.useToken();
    const isEdit = Boolean(chartToEdit?.id);

    const { items: functions = [], loading: fnLoading } = useSelector(s => s.reportFunctionList);
    const categoryLabelMaps = useCategoryLabelMaps();

    const visibleFunctions = functions.filter(fn => {
        if (fn.enabled === false) return false;
        const fnDs = fn.dataSourceId ?? fn.DataSourceId;
        const fnSchema = fn.schema ?? fn.Schema ?? 'public';
        if (dataSourceId && fnDs !== dataSourceId) return false;
        if (schema && fnSchema !== schema) return false;
        return true;
    });

    const [selectedFn, setSelectedFn] = useState(null);
    const [chartTitle, setChartTitle] = useState('');
    const [chartDescription, setChartDescription] = useState('');
    const [vizType, setVizType] = useState('table');
    const [datasetKey, setDatasetKey] = useState('default');
    const [encoding, setEncoding] = useState({});
    const [defaultDisplayMode, setDefaultDisplayMode] = useState('chart');
    const [seriesColors, setSeriesColors] = useState([...DEFAULT_SERIES_COLORS]);
    const [statDisplay, setStatDisplay] = useState(STAT_DISPLAY_AUTO);
    const [previewResult, setPreviewResult] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [dimensionFilter, setDimensionFilter] = useState(null);
    const previewRequestRef = useRef(0);

    const runPreview = useCallback(async (fn, { preserveDimension = false } = {}) => {
        if (!fn) return;
        const fnId = fn.id ?? fn.Id;
        if (!fnId) return;

        const requestId = previewRequestRef.current + 1;
        previewRequestRef.current = requestId;

        setPreviewLoading(true);
        setPreviewResult(null);

        const chartDef = chartToEdit
            && String(chartToEdit.functionId) === String(fnId)
            ? chartToEdit
            : {};
        const filter = buildChartExecuteFilter(chartDef, dateRange, globalFilters);
        const result = await dispatch(executeReportFunction(fnId, filter));

        if (requestId !== previewRequestRef.current) return;

        setPreviewResult(result ?? null);
        if (result?.datasets) {
            const keys = Object.keys(result.datasets);
            const key = keys.includes(datasetKey) ? datasetKey : (keys[0] ?? 'default');
            if (key !== datasetKey) setDatasetKey(key);
            const cols = result.datasets[key]?.columns ?? [];
            setEncoding(prev => (Object.keys(prev).length > 0 ? prev : inferChartEncoding(cols)));
            if (!preserveDimension) {
                const dims = listDatasetDimensions(result.datasets[key]);
                if (dims.length === 1) {
                    setDimensionFilter(dims[0]);
                } else {
                    setDimensionFilter(null);
                }
            }
        }
        setPreviewLoading(false);
    }, [dispatch, datasetKey, dateRange, globalFilters, chartToEdit]);

    const handleSelectFn = (fn) => {
        setSelectedFn(fn);
        setChartTitle(prev => prev.trim() || (fn.displayName ?? fn.DisplayName ?? ''));
        setChartDescription(prev => prev.trim() || (fn.description ?? fn.Description ?? ''));
        setEncoding({});
        setDimensionFilter(null);
        setVizType('table');
        setPreviewResult(null);
        runPreview(fn);
    };

    useEffect(() => {
        if (!open) return;
        dispatch(listReportFunctions());
        if (!chartToEdit) {
            setSelectedFn(null);
            setChartTitle('');
            setChartDescription('');
            setVizType('table');
            setEncoding({});
            setDefaultDisplayMode('table');
            setSeriesColors([...DEFAULT_SERIES_COLORS]);
            setStatDisplay(STAT_DISPLAY_AUTO);
            setPreviewResult(null);
            setDimensionFilter(null);
        }
    }, [open, chartToEdit, dispatch]);

    useEffect(() => {
        if (!open || !chartToEdit) return;
        setChartTitle(chartToEdit.title ?? '');
        setChartDescription(chartToEdit.description ?? '');
        setVizType(chartToEdit.viz?.type ?? 'table');
        setDatasetKey(chartToEdit.datasetKey ?? 'default');
        setDimensionFilter(chartToEdit.dimensionFilter ?? null);
        setEncoding(chartToEdit.viz?.encoding ?? {});
        setStatDisplay(chartToEdit.viz?.statDisplay ?? STAT_DISPLAY_AUTO);
        setDefaultDisplayMode(
            chartToEdit.viz?.mode
            ?? (chartToEdit.viz?.type === 'table' ? 'table' : 'chart'),
        );
        const savedColors = chartToEdit.viz?.colors;
        setSeriesColors(
            Array.isArray(savedColors) && savedColors.length > 0
                ? [...savedColors]
                : [...DEFAULT_SERIES_COLORS],
        );
        const fn = functions.find(f => (f.id ?? f.Id) === chartToEdit.functionId);
        if (fn) {
            setSelectedFn(fn);
            runPreview(fn, { preserveDimension: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, chartToEdit?.id, functions.length, dateRange, globalFilters]);

    useEffect(() => {
        if (!open || chartToEdit || selectedFn || fnLoading || visibleFunctions.length === 0) return;
        handleSelectFn(visibleFunctions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, chartToEdit, fnLoading, visibleFunctions.length, dataSourceId]);

    const datasets = previewResult?.datasets ?? {};
    const rawDataset = datasets[datasetKey];
    const dimensionOptions = useMemo(
        () => listDatasetDimensions(rawDataset),
        [rawDataset],
    );
    const currentDataset = useMemo(
        () => processChartDataset({ dimensionFilter, viz: { encoding } }, rawDataset, categoryLabelMaps),
        [dimensionFilter, rawDataset, categoryLabelMaps, encoding],
    );
    const datasetShape = useMemo(
        () => detectDatasetShape(rawDataset?.columns ?? []),
        [rawDataset],
    );
    const vizOptions = useMemo(() => vizOptionsForShape(datasetShape), [datasetShape]);

    useEffect(() => {
        if (!open || !rawDataset) return;
        if (isHierarchicalDataset(rawDataset.columns) && !HIERARCHY_VIZ_TYPES.has(vizType)) {
            setVizType('treemap');
        }
    }, [open, rawDataset, vizType]);
    const columns = currentDataset?.columns ?? [];
    const catColOptions = columnOptions(columns.length > 0 ? columns : []);
    const volColOptions = columnOptions(
        columns.filter(c => c.type === 'number').length > 0
            ? columns.filter(c => c.type === 'number')
            : columns,
    );
    const datasetKeys = Object.keys(datasets);

    const resolvedEncoding = () => {
        if (encoding.category && encodingHasVolume(encoding)) return encoding;
        return inferChartEncoding(columns);
    };

    const handleSaveChart = () => {
        if (!selectedFn) return;
        if (dimensionOptions.length > 0 && !dimensionFilter) {
            message.warning(t('Pick which summary dimension this chart uses (e.g. age_band, quality)'));
            return;
        }
        const enc = resolvedEncoding();
        if (vizType === STAT_VIZ && !encodingHasVolume(enc)) {
            message.warning(t('Pick a volume column for the count card'));
            return;
        }
        if (vizType !== 'table' && vizType !== STAT_VIZ && (!enc.category || !encodingHasVolume(enc))) {
            message.warning(t('Pick a category column and at least one volume column'));
            return;
        }
        if (SINGLE_METRIC_VIZ.has(vizType) && normalizeVolumeEncoding(enc.value).length > 1) {
            message.warning(t('Pie, donut, and treemap use the first volume column only'));
        }
        const vols = normalizeVolumeEncoding(enc.value);
        const encodingOut = {
            ...enc,
            value: SINGLE_METRIC_VIZ.has(vizType) ? vols[0] : (vols.length === 1 ? vols[0] : vols),
        };
        const colors = seriesColors.map(c => (typeof c === 'string' ? c : c?.toHexString?.() ?? String(c))).filter(Boolean);
        const chartDef = {
            id: chartToEdit?.id ?? crypto.randomUUID(),
            title: chartTitle.trim() || (selectedFn.displayName ?? selectedFn.DisplayName),
            description: chartDescription.trim() || null,
            functionId: selectedFn.id ?? selectedFn.Id,
            datasetKey,
            dimensionFilter: dimensionFilter || null,
            viz: enrichVizSpec({
                type: vizType,
                mode: defaultDisplayMode === 'table' && vizType === STAT_VIZ ? 'chart' : defaultDisplayMode,
                encoding: encodingOut,
                colors,
                ...(vizType === STAT_VIZ ? { statDisplay } : {}),
            }, dimensionFilter),
            layout: chartToEdit?.layout ?? (
                vizType === STAT_VIZ
                    ? { x: 0, y: Infinity, w: 3, h: 6 }
                    : { x: 0, y: Infinity, w: 6, h: 10 }
            ),
        };
        onSave(chartDef);
        message.success(isEdit ? t('Chart updated') : t('Chart added to dashboard'));
        onClose();
    };

    const volumeValues = normalizeVolumeEncoding(encoding.value);
    const multiVolume = !SINGLE_METRIC_VIZ.has(vizType);
    const awaitingDimension = dimensionOptions.length > 0 && !dimensionFilter;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="bottom"
            height="80vh"
            title={isEdit ? t('Edit chart') : t('Add chart')}
            extra={(
                <Space>
                    <Button
                        icon={<IconReload />}
                        disabled={!selectedFn}
                        loading={previewLoading}
                        onClick={() => selectedFn && runPreview(selectedFn)}
                    >
                        {t('Refresh preview')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveFilled />}
                        disabled={!selectedFn}
                        onClick={handleSaveChart}
                    >
                        {t('Save chart')}
                    </Button>
                </Space>
            )}
            styles={{ body: { padding: 0, overflow: 'hidden', height: '100%' } }}
            zIndex={1100}
        >
            <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
                <div style={{
                    width: FN_LIST_W,
                    flexShrink: 0,
                    borderRight: `1px solid ${token.colorBorderSecondary}`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        padding: '8px 12px',
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillQuaternary,
                        flexShrink: 0,
                    }}>
                        <Text strong style={{ fontSize: 12 }}>{t('Select a function')}</Text>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
                        {fnLoading ? (
                            <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
                        ) : visibleFunctions.length === 0 ? (
                            <Empty
                                description={
                                    functions.length === 0
                                        ? t('No report functions yet. Open SQL Lab in the data catalog, set names, and click Save function.')
                                        : t('No functions for this database/schema. Select another data source or save a function in SQL Lab.')
                                }
                                style={{ padding: 16 }}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ) : (
                            visibleFunctions.map(fn => {
                                const fnId = fn.id ?? fn.Id;
                                const isActive = (selectedFn?.id ?? selectedFn?.Id) === fnId;
                                return (
                                    <div
                                        key={fnId}
                                        onClick={() => handleSelectFn(fn)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={e => e.key === 'Enter' && handleSelectFn(fn)}
                                        style={{
                                            padding: '8px 10px',
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            marginBottom: 3,
                                            background: isActive ? token.colorPrimaryBg : 'transparent',
                                            border: isActive ? `1px solid ${token.colorPrimaryBorder}` : '1px solid transparent',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <IconCode style={{ fontSize: 14, flexShrink: 0 }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {fn.displayName ?? fn.DisplayName}
                                                </div>
                                                <div style={{ fontSize: 11, color: token.colorTextSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {(fn.schema ?? fn.Schema)}.{(fn.functionName ?? fn.FunctionName)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', padding: 16 }}>
                    {!selectedFn ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: token.colorTextSecondary }}>
                            {t('Select a function on the left to preview')}
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: 200 }}>
                                    <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>{t('Chart title')}</Text>
                                    <Input
                                        size="large"
                                        style={{ marginTop: 4 }}
                                        value={chartTitle}
                                        onChange={e => setChartTitle(e.target.value)}
                                        placeholder={t('Shown on the dashboard card')}
                                    />
                                </div>
                                <div style={{ flex: 1, minWidth: 200 }}>
                                    <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>{t('Description')}</Text>
                                    <Input.TextArea
                                        rows={2}
                                        style={{ marginTop: 4 }}
                                        value={chartDescription}
                                        onChange={e => setChartDescription(e.target.value)}
                                        placeholder={t('Optional subtitle under the chart title')}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                                <Radio.Group
                                    size="small"
                                    value={vizType}
                                    onChange={e => setVizType(e.target.value)}
                                    buttonStyle="solid"
                                >
                                    {vizOptions.map(v => (
                                        <Radio.Button key={v.value} value={v.value}>{v.label}</Radio.Button>
                                    ))}
                                </Radio.Group>
                                <Radio.Group
                                    size="small"
                                    value={defaultDisplayMode}
                                    onChange={e => setDefaultDisplayMode(e.target.value)}
                                >
                                    <Text type="secondary" style={{ fontSize: 12, marginRight: 6 }}>{t('Default view')}:</Text>
                                    {DISPLAY_MODE_OPTIONS.map(m => (
                                        <Radio.Button key={m.value} value={m.value}>{t(m.label)}</Radio.Button>
                                    ))}
                                </Radio.Group>
                                {datasetKeys.length > 1 && (
                                    <Select
                                        size="small"
                                        value={datasetKey}
                                        onChange={k => {
                                            setDatasetKey(k);
                                            const cols = datasets[k]?.columns ?? [];
                                            setEncoding(inferChartEncoding(cols));
                                        }}
                                        options={datasetKeys.map(k => ({ value: k, label: k }))}
                                        style={{ minWidth: 120 }}
                                    />
                                )}
                            </div>

                            {dimensionOptions.length > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                        {t('Summary dimension')} *
                                    </Text>
                                    <Select
                                        size="large"
                                        style={{ width: '100%', maxWidth: 360 }}
                                        placeholder={t('Select dimension slice')}
                                        value={dimensionFilter ?? undefined}
                                        onChange={setDimensionFilter}
                                        options={dimensionOptions.map(d => ({ value: d, label: d }))}
                                    />
                                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                                        {t('Multi-dimension functions return several slices; pick one per chart (e.g. age_band for Age band).')}
                                    </Text>
                                </div>
                            )}

                            {currentDataset && (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12, fontSize: 12 }}
                                    message={datasetShape === 'hierarchy'
                                        ? t('Hierarchical data: use treemap to drill down (ECharts), or table for the full tree rows.')
                                        : vizType === 'table'
                                        ? t('Table view shows all columns')
                                        : vizType === STAT_VIZ
                                            ? t('Count card shows totals from the volume column. Category is optional (labels each count). Dashboard filters apply automatically.')
                                            : t('Pick category and volume column(s). Cohort, upload batch, and dates use the dashboard filters above the chart grid.')}
                                />
                            )}

                            {currentDataset && vizType === STAT_VIZ && (
                                <div style={{ marginBottom: 12 }}>
                                    <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>{t('Count display')}</Text>
                                    <Select
                                        size="large"
                                        style={{ width: '100%', maxWidth: 360 }}
                                        value={statDisplay}
                                        onChange={setStatDisplay}
                                        options={[
                                            { value: STAT_DISPLAY_AUTO, label: t('Auto (single total or grid)') },
                                            { value: STAT_DISPLAY_TOTAL, label: t('Single total') },
                                            { value: STAT_DISPLAY_BREAKDOWN, label: t('Grid by category') },
                                        ]}
                                    />
                                </div>
                            )}

                            {currentDataset && datasetShape !== 'hierarchy' && vizType !== 'table' && vizType !== STAT_VIZ && (
                                <div style={{ marginBottom: 12 }}>
                                    <Text style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>{t('Series colors')}</Text>
                                    <Space wrap size={8}>
                                        {seriesColors.map((color, idx) => (
                                            <Space key={idx} size={4}>
                                                <ColorPicker
                                                    size="small"
                                                    value={color}
                                                    onChange={(_, hex) => {
                                                        setSeriesColors(prev => {
                                                            const next = [...prev];
                                                            next[idx] = hex;
                                                            return next;
                                                        });
                                                    }}
                                                />
                                                {seriesColors.length > 1 && (
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => setSeriesColors(prev => prev.filter((_, i) => i !== idx))}
                                                    />
                                                )}
                                            </Space>
                                        ))}
                                        <Button
                                            type="dashed"
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={() => setSeriesColors(prev => [...prev, DEFAULT_SERIES_COLORS[prev.length % DEFAULT_SERIES_COLORS.length]])}
                                        >
                                            {t('Add color')}
                                        </Button>
                                    </Space>
                                </div>
                            )}

                            {currentDataset && vizType !== 'table' && (
                                <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
                                    <Col xs={24} sm={12}>
                                        <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                            {t('Category column')}
                                            {vizType === STAT_VIZ ? ` (${t('optional')})` : ' *'}
                                        </Text>
                                        <Select
                                            size="large"
                                            style={{ width: '100%' }}
                                            placeholder={t('Select column')}
                                            value={encoding.category}
                                            onChange={v => setEncoding(e => ({ ...e, category: v }))}
                                            options={catColOptions}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                            {multiVolume ? t('Volume columns') : t('Volume column')} *
                                        </Text>
                                        <Select
                                            mode={multiVolume ? 'multiple' : undefined}
                                            size="large"
                                            style={{ width: '100%' }}
                                            placeholder={multiVolume ? t('Select one or more') : t('Select column')}
                                            value={multiVolume ? volumeValues : (volumeValues[0] ?? undefined)}
                                            onChange={v => setEncoding(e => ({ ...e, value: v }))}
                                            options={volColOptions}
                                            maxTagCount="responsive"
                                        />
                                    </Col>
                                </Row>
                            )}

                            <div style={{
                                flex: 1,
                                border: `1px solid ${token.colorBorderSecondary}`,
                                borderRadius: 8,
                                minHeight: 280,
                                padding: 0,
                                overflow: 'hidden',
                                background: token.colorBgContainer,
                            }}>
                                <ChartRenderer
                                    dataset={awaitingDimension ? null : currentDataset}
                                    vizSpec={enrichVizSpec({
                                        type: vizType,
                                        mode: defaultDisplayMode,
                                        encoding: resolvedEncoding(),
                                        colors: seriesColors,
                                        ...(vizType === STAT_VIZ ? { statDisplay } : {}),
                                    }, dimensionFilter)}
                                    defaultMode={defaultDisplayMode}
                                    title={chartTitle}
                                    loading={previewLoading}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Drawer>
    );
}
